import { useEffect, useState, useRef } from "react";
import { Col, Container, Row, Spinner, ListGroup, Form, InputGroup, Badge, Alert, Button } from "react-bootstrap";
import { t } from "i18next";
import { Folder, emptyFolder } from "../../../../interfaces/folder";
import { Document } from "../../../../interfaces/document";
import { getFolders, deleteFolder, updateFolder, bulkMoveFolder, bulkDeleteFolder, getFolderPath, downloadFolderAsBlob } from "../../../../services/folder";
import { getDocuments, deleteDocument, downloadDocumentAsBlob, updateDocument, bulkMoveDocument, bulkDeleteDocument, bulkUploadDocuments, bulkDownloadAsBlob } from "../../../../services/document";
import { getUser } from "../../../../services/user";
import { FolderModal } from "./folder-modal";
import { DocumentModal } from "./document-modal";
import { ShareModal } from "./share-modal";
import { BreadcrumbNav } from "./breadcrumb-nav";
import moment from 'moment';

interface FileManagerProps {
    userRole: string;
    isAdmin?: boolean;
}

interface UserOption {
    id: number;
    name: string;
    email: string;
    teams?: string[];
    clubs?: string[];
    roles?: string[];
}

interface ContextMenu {
    x: number;
    y: number;
    type: 'folder' | 'document' | 'background';
    item?: Folder | Document;
}

type SortBy = 'name' | 'date' | 'size';
type SortOrder = 'asc' | 'desc';

export function FileManager({ userRole, isAdmin = false }: FileManagerProps) {
    const [folders, setFolders] = useState<Folder[]>([]);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
    const [folderPath, setFolderPath] = useState<Folder[]>([]);
    const [loading, setLoading] = useState(false);
    const [sidebarLoaded, setSidebarLoaded] = useState(false);

    // All folders for sidebar
    const [allFolders, setAllFolders] = useState<Folder[]>([]);
    const [sharedFolders, setSharedFolders] = useState<Folder[]>([]);
    const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
    const [expandedSharedFolders, setExpandedSharedFolders] = useState<Set<number>>(new Set());

    // Admin: viewing other user's documents
    const [viewingUserId, setViewingUserId] = useState<number | null>(null);
    const [viewingUser, setViewingUser] = useState<UserOption | null>(null);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userSearchResults, setUserSearchResults] = useState<UserOption[]>([]);
    const [searchingUsers, setSearchingUsers] = useState(false);

    // Search and sort
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<SortBy>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    // Context menu
    const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);

    // Drag & Drop (folders/documents)
    const [draggedItem, setDraggedItem] = useState<
        | { type: 'folder', item: Folder }
        | { type: 'document', item: Document }
        | { type: 'multiple', item: null }
        | null
    >(null);
    const [dropTarget, setDropTarget] = useState<number | null>(null);

    // Drag & Drop (files from explorer)
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);

    // Multi-select
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

    // Modals
    const [showFolderModal, setShowFolderModal] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
    const [editingDocument, setEditingDocument] = useState<Document | null>(null);
    const [shareItem, setShareItem] = useState<{ type: 'folder' | 'document', item: Folder | Document } | null>(null);

    const contentAreaRef = useRef<HTMLDivElement>(null);

    const canWrite = userRole === 'manager' || userRole === 'coach';

    // Verificar si la carpeta actual permite escritura
    // Si estamos en la raíz (null), solo permitir si canWrite es true
    // Si estamos en una carpeta, verificar que sea del usuario (is_owner)
    const canWriteInCurrentFolder = (() => {
        if (!canWrite) return false;
        if (currentFolderId === null) return true; // Raíz de "Mis documentos"

        // Buscar la carpeta actual en allFolders o sharedFolders
        const currentFolder = [...allFolders, ...sharedFolders].find(f => f.id === currentFolderId);
        if (!currentFolder) return true; // Por defecto permitir si no encontramos la carpeta

        return currentFolder.is_owner; // Solo permitir si es el propietario
    })();

    useEffect(() => {
        loadAllFolders();
    }, [viewingUserId]);

    useEffect(() => {
        if (sidebarLoaded) {
            loadData();
        }
    }, [currentFolderId, sidebarLoaded, viewingUserId]);

    // Close context menu on click outside
    useEffect(() => {
        const handleClick = () => setContextMenu(null);
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    const loadAllFolders = async () => {
        try {
            const res = await getFolders(null, viewingUserId);

            // Cuando visualizamos documentos de otro usuario, todas son "propias" para ese usuario
            if (viewingUserId) {
                await loadFolderTree(res.data);
                setSharedFolders([]);
            } else {
                // Separar carpetas propias de compartidas usando is_owner
                const ownFolders = res.data.filter((f: Folder) => f.is_owner);
                const shared = res.data.filter((f: Folder) => !f.is_owner);

                await loadFolderTree(ownFolders);
                await loadSharedFolderTree(shared);
            }

            // Marcar sidebar como cargado para que se pueda cargar el contenido
            setSidebarLoaded(true);
        } catch (error) {
            setSidebarLoaded(true); // Marcar como cargado incluso si hay error
        }
    };

    const loadFolderTree = async (rootFolders: Folder[]) => {
        const allFoldersMap: Record<number, Folder> = {};

        rootFolders.forEach(folder => {
            allFoldersMap[folder.id] = folder;
        });

        const loadSubfolders = async (folderId: number) => {
            try {
                const res = await getFolders(folderId, viewingUserId);
                res.data.forEach((folder: Folder) => {
                    // Si estamos viendo como otro usuario, añadir todas las carpetas
                    if (viewingUserId || folder.is_owner) {
                        allFoldersMap[folder.id] = folder;
                    }
                });
                const foldersToLoad = viewingUserId
                    ? res.data
                    : res.data.filter((f: Folder) => f.is_owner);
                await Promise.all(foldersToLoad.map((folder: Folder) => loadSubfolders(folder.id)));
            } catch (error) {
            }
        };

        await Promise.all(rootFolders.map(folder => loadSubfolders(folder.id)));
        setAllFolders(Object.values(allFoldersMap));
    };

    const loadSharedFolderTree = async (rootFolders: Folder[]) => {
        const sharedFoldersMap: Record<number, Folder> = {};

        rootFolders.forEach(folder => {
            sharedFoldersMap[folder.id] = folder;
        });

        const loadSubfolders = async (folderId: number) => {
            try {
                const res = await getFolders(folderId, viewingUserId);
                res.data.forEach((folder: Folder) => {
                    sharedFoldersMap[folder.id] = folder;
                });
                await Promise.all(res.data.map((folder: Folder) => loadSubfolders(folder.id)));
            } catch (error) {
            }
        };

        await Promise.all(rootFolders.map(folder => loadSubfolders(folder.id)));
        setSharedFolders(Object.values(sharedFoldersMap));
    };

    const loadData = () => {
        setLoading(true);

        Promise.all([
            getFolders(currentFolderId, viewingUserId),
            getDocuments(currentFolderId, viewingUserId)
        ])
        .then(([foldersRes, docsRes]) => {
            // Si estamos en la raíz (currentFolderId === null) y no vemos otro usuario, solo mostrar carpetas propias
            let filteredFolders = foldersRes.data;
            if (currentFolderId === null && !viewingUserId) {
                filteredFolders = foldersRes.data.filter((f: Folder) => f.is_owner);
            }

            setFolders(filteredFolders);
            setDocuments(docsRes.data);
        })
        .catch(error => {
        })
        .finally(() => {
            setLoading(false);
        });
    };

    // Admin: Search for users
    const handleUserSearch = async (searchValue: string) => {
        setUserSearchTerm(searchValue);

        if (searchValue.length < 2) {
            setUserSearchResults([]);
            return;
        }

        setSearchingUsers(true);
        try {
            const res = await getUser(searchValue);
            setUserSearchResults(res.data.map((u: any) => {
                // Mapear roles a nombres legibles
                const roleNames: string[] = [];
                if (u.role && Array.isArray(u.role)) {
                    u.role.forEach((r: any) => {
                        const roleId = r.id || r;
                        if (roleId === 'padeltech_administrator') roleNames.push(t('Administrator'));
                        else if (roleId === 'padeltech_manager') roleNames.push(t('Manager'));
                        else if (roleId === 'padeltech_coach') roleNames.push(t('Coach'));
                        else if (roleId === 'padeltech_athlete') roleNames.push(t('Athlete'));
                    });
                }
                return {
                    id: u.ID || u.id,
                    name: u.display_name || u.name,
                    email: u.user_email || u.email,
                    teams: u.teams ? u.teams.map((t: any) => t.name || t) : [],
                    clubs: u.club ? u.club.map((c: any) => c.name || c) : [],
                    roles: roleNames
                };
            }));
        } catch (error) {
            setUserSearchResults([]);
        } finally {
            setSearchingUsers(false);
        }
    };

    // Admin: Select a user to view their documents
    const handleSelectUser = (user: UserOption) => {
        setViewingUserId(user.id);
        setViewingUser(user);
        setUserSearchTerm('');
        setUserSearchResults([]);
        setCurrentFolderId(null);
        setFolderPath([]);
        setSidebarLoaded(false);
    };

    // Admin: Go back to own documents
    const handleBackToMyDocuments = () => {
        setViewingUserId(null);
        setViewingUser(null);
        setCurrentFolderId(null);
        setFolderPath([]);
        setSidebarLoaded(false);
    };

    const handleFolderClick = (folder: Folder) => {
        setCurrentFolderId(folder.id);
        // Load breadcrumb path from API
        getFolderPath(folder.id)
            .then(res => {
                // Ensure we have an array
                const path = Array.isArray(res.data) ? res.data : (res.data.path ? res.data.path : []);
                setFolderPath(path);
            })
            .catch(error => {
                // Fallback to appending folder
                setFolderPath([...folderPath, folder]);
            });
        setExpandedFolders(prev => {
            const newSet = new Set(prev);
            newSet.add(folder.id);
            return newSet;
        });
    };

    const handleFolderClickFromSidebar = (folder: Folder) => {
        setCurrentFolderId(folder.id);
        // Load breadcrumb path from API
        getFolderPath(folder.id)
            .then(res => {
                // Ensure we have an array
                const path = Array.isArray(res.data) ? res.data : (res.data.path ? res.data.path : []);
                setFolderPath(path);
            })
            .catch(error => {
                // Fallback to local path building
                const path = buildFolderPathLocal(folder.id);
                setFolderPath(path);
            });
        setExpandedFolders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(folder.id)) {
                newSet.delete(folder.id);
            } else {
                newSet.add(folder.id);
            }
            return newSet;
        });
    };

    // Fallback function for local path building (used only if API fails)
    const buildFolderPathLocal = (folderId: number): Folder[] => {
        const path: Folder[] = [];
        let currentId: number | null = folderId;

        while (currentId !== null) {
            const folder = allFolders.find(f => f.id === currentId);
            if (folder) {
                path.unshift(folder);
                currentId = folder.parent_id;
            } else {
                break;
            }
        }

        return path;
    };

    const handleBreadcrumbClick = (index: number) => {
        if (index === -1) {
            setCurrentFolderId(null);
            setFolderPath([]);
        } else {
            const newPath = folderPath.slice(0, index + 1);
            setCurrentFolderId(newPath[newPath.length - 1].id);
            setFolderPath(newPath);
        }
    };

    const handleContextMenu = (e: React.MouseEvent, type: 'folder' | 'document' | 'background', item?: Folder | Document) => {
        e.preventDefault();
        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            type,
            item
        });
    };

    const handleNewFolder = () => {
        setEditingFolder(null);
        setShowFolderModal(true);
        setContextMenu(null);
    };

    const handleNewSubfolder = (parentFolder: Folder) => {
        // Crear una carpeta parcial con el parent_id establecido
        const newSubfolder: Folder = {
            ...emptyFolder,
            parent_id: parentFolder.id
        };
        setEditingFolder(newSubfolder);
        setShowFolderModal(true);
        setContextMenu(null);
    };

    const handleEditFolder = (folder: Folder) => {
        setEditingFolder(folder);
        setShowFolderModal(true);
        setContextMenu(null);
    };

    const handleDeleteFolder = (folder: Folder) => {
        if (!window.confirm(t('Are you sure you want to delete this folder?'))) {
            return;
        }

        deleteFolder(folder.id)
            .then(() => {
                loadData();
                loadAllFolders();
            })
            .catch(error => {
                alert(error.response?.data?.message || t('Error deleting folder'));
            });
        setContextMenu(null);
    };

    const handleDeleteDocument = (document: Document) => {
        if (!window.confirm(t('Are you sure you want to delete this document?'))) {
            return;
        }

        deleteDocument(document.id)
            .then(() => {
                loadData();
            })
            .catch(error => {
                alert(error.response?.data?.message || t('Error deleting document'));
            });
        setContextMenu(null);
    };

    const handleDownloadDocument = async (document: Document) => {
        try {
            await downloadDocumentAsBlob(document.id, document.name, document.mime_type);
        } catch (error: any) {
            alert(error.message || t('Error downloading document'));
        }
    };

    const handleEditDocument = (document: Document) => {
        setEditingDocument(document);
        setShowDocumentModal(true);
        setContextMenu(null);
    };

    const handleDownloadFolder = async (folder: Folder) => {
        try {
            await downloadFolderAsBlob(folder.id, folder.name);
        } catch (error: any) {
            alert(error.message || t('Error downloading folder'));
        }
        setContextMenu(null);
    };

    const handleBulkDownload = async () => {
        if (selectedItems.size === 0) return;

        const selectedFolders: number[] = [];
        const selectedDocuments: number[] = [];

        selectedItems.forEach(key => {
            const [type, id] = key.split('-');
            if (type === 'folder') {
                selectedFolders.push(parseInt(id));
            } else {
                selectedDocuments.push(parseInt(id));
            }
        });

        try {
            // If only one document and no folders, download directly without ZIP
            if (selectedDocuments.length === 1 && selectedFolders.length === 0) {
                const doc = documents.find(d => d.id === selectedDocuments[0]);
                if (doc) {
                    await handleDownloadDocument(doc);
                }
            } else {
                // Otherwise download as ZIP
                await bulkDownloadAsBlob(selectedFolders, selectedDocuments);
            }
        } catch (error: any) {
            alert(error.message || t('Error downloading items'));
        }
    };

    const handleShare = (type: 'folder' | 'document', item: Folder | Document) => {
        setShareItem({ type, item });
        setShowShareModal(true);
        setContextMenu(null);
    };

    // Drag & Drop handlers
    const handleDragStart = (e: React.DragEvent, type: 'folder' | 'document', item: Folder | Document) => {
        if (!canWriteInCurrentFolder) return;

        const itemKey = getItemKey(type, item.id);

        // If dragging a selected item and there are multiple selections, drag all selected items
        if (selectedItems.has(itemKey) && selectedItems.size > 1) {
            // Mark that we're dragging multiple items
            setDraggedItem({ type: 'multiple', item: null });
            e.dataTransfer.effectAllowed = 'move';
        } else {
            // Regular single item drag
            if (type === 'folder') {
                setDraggedItem({ type: 'folder', item: item as Folder });
            } else {
                setDraggedItem({ type: 'document', item: item as Document });
            }
            e.dataTransfer.effectAllowed = 'move';
            // Clear selection when dragging a non-selected item
            if (!selectedItems.has(itemKey)) {
                handleClearSelection();
            }
        }
    };

    const isDescendantFolder = (parentId: number, childId: number): boolean => {
        let currentId: number | null = childId;
        while (currentId !== null) {
            if (currentId === parentId) return true;
            const folder = allFolders.find(f => f.id === currentId);
            currentId = folder?.parent_id || null;
        }
        return false;
    };

    const handleDragOver = (e: React.DragEvent, targetFolderId: number | null) => {
        e.preventDefault();
        if (!draggedItem) return;

        // Don't allow dropping a folder into itself or its descendants
        if (draggedItem.type === 'folder') {
            const folderId = (draggedItem.item as Folder).id;
            if (folderId === targetFolderId || (targetFolderId !== null && isDescendantFolder(folderId, targetFolderId))) {
                e.dataTransfer.dropEffect = 'none';
                return;
            }
        }

        e.dataTransfer.dropEffect = 'move';
        setDropTarget(targetFolderId);
    };

    const handleDragLeave = () => {
        setDropTarget(null);
    };

    const handleDrop = async (e: React.DragEvent, targetFolderId: number | null) => {
        e.preventDefault();
        setDropTarget(null);

        if (!draggedItem) return;

        // Verificar que el destino permite escritura
        if (targetFolderId !== null) {
            const targetFolder = [...allFolders, ...sharedFolders].find(f => f.id === targetFolderId);
            if (targetFolder && !targetFolder.is_owner) {
                alert(t('You cannot move items to a shared folder'));
                setDraggedItem(null);
                return;
            }
        }

        try {
            // Handle multiple item drop
            if (draggedItem.type === 'multiple') {
                await handleBulkMove(targetFolderId);
            }
            // Handle single folder drop
            else if (draggedItem.type === 'folder') {
                const folder = draggedItem.item as Folder;
                const updatePayload = { id: folder.id, parent_id: targetFolderId };
                const updateResult = await updateFolder(updatePayload);

                // Reload both the sidebar tree and current folder content
                await loadAllFolders();
                await loadData();
            }
            // Handle single document drop
            else {
                const document = draggedItem.item as Document;
                await updateDocument({ id: document.id, folder_id: targetFolderId });
                await loadData();
            }
        } catch (error: any) {
            alert(error.response?.data?.message || t(draggedItem.type === 'folder' ? 'Error moving folder' : 'Error moving document'));
        } finally {
            setDraggedItem(null);
        }
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDropTarget(null);
    };

    // File drag & drop from explorer
    const handleFileDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Only handle if dragging files from outside (not internal drag) AND can write in current folder
        if (!draggedItem && e.dataTransfer.types.includes('Files') && canWriteInCurrentFolder) {
            setIsDraggingFiles(true);
        }
    };

    const handleFileDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Only reset if leaving the content area
        if (e.target === contentAreaRef.current) {
            setIsDraggingFiles(false);
        }
    };

    const handleFileDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingFiles(false);

        if (!canWriteInCurrentFolder || uploadingFiles) return;

        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) return;

        setUploadingFiles(true);

        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files[]', file);
            });

            if (currentFolderId) {
                formData.append('folder_id', currentFolderId.toString());
            }

            const response = await bulkUploadDocuments(formData);

            const { success, failed, results } = response.data;

            // Show results
            if (failed > 0) {
                const errors = results
                    .filter((r: any) => r.status === 'error')
                    .map((r: any) => `${r.filename}: ${r.error}`)
                    .join('\n');
                alert(t('Some files failed to upload') + ':\n\n' + errors);
            }

            if (success > 0) {
                // Reload documents list
                loadData();
            }
        } catch (error: any) {
            alert(error.response?.data?.message || t('Error uploading files'));
        } finally {
            setUploadingFiles(false);
        }
    };

    // Multi-select handlers
    const getItemKey = (type: 'folder' | 'document', id: number) => `${type}-${id}`;

    const handleItemSelect = (e: React.MouseEvent, type: 'folder' | 'document', item: Folder | Document, index: number) => {
        const itemKey = getItemKey(type, item.id);

        if (e.ctrlKey || e.metaKey) {
            // Ctrl/Cmd+Click: Toggle selection
            setSelectedItems(prev => {
                const newSet = new Set(prev);
                if (newSet.has(itemKey)) {
                    newSet.delete(itemKey);
                } else {
                    newSet.add(itemKey);
                }
                return newSet;
            });
            setLastSelectedIndex(index);
        } else if (e.shiftKey && lastSelectedIndex !== null) {
            // Shift+Click: Select range
            const start = Math.min(lastSelectedIndex, index);
            const end = Math.max(lastSelectedIndex, index);
            const itemsInRange = filteredItems.slice(start, end + 1);

            setSelectedItems(prev => {
                const newSet = new Set(prev);
                itemsInRange.forEach(({ type: itemType, item: itemInRange }) => {
                    newSet.add(getItemKey(itemType, itemInRange.id));
                });
                return newSet;
            });
        } else {
            // Regular click: Select only this item
            setSelectedItems(new Set([itemKey]));
            setLastSelectedIndex(index);
        }
    };

    const handleCheckboxChange = (type: 'folder' | 'document', id: number) => {
        const itemKey = getItemKey(type, id);
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemKey)) {
                newSet.delete(itemKey);
            } else {
                newSet.add(itemKey);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        if (selectedItems.size === filteredItems.length) {
            setSelectedItems(new Set());
        } else {
            const allKeys = filteredItems.map(({ type, item }) => getItemKey(type, item.id));
            setSelectedItems(new Set(allKeys));
        }
    };

    const handleClearSelection = () => {
        setSelectedItems(new Set());
        setLastSelectedIndex(null);
    };

    const handleBulkDelete = async () => {
        if (selectedItems.size === 0) return;

        if (!window.confirm(t('Are you sure you want to delete the selected items?'))) {
            return;
        }

        const selectedFolders: number[] = [];
        const selectedDocuments: number[] = [];

        selectedItems.forEach(key => {
            const [type, id] = key.split('-');
            if (type === 'folder') {
                selectedFolders.push(parseInt(id));
            } else {
                selectedDocuments.push(parseInt(id));
            }
        });

        try {
            let totalFailed = 0;
            const errorMessages: string[] = [];

            // Delete folders using bulk endpoint
            if (selectedFolders.length > 0) {
                const folderResponse = await bulkDeleteFolder(selectedFolders);
                const { success, failed, results } = folderResponse.data;
                totalFailed += failed;

                if (failed > 0) {
                    const folderErrors = results
                        .filter((r: any) => r.status === 'error')
                        .map((r: any) => {
                            const folder = folders.find(f => f.id === r.folder_id);
                            return `${folder?.name || 'Folder ' + r.folder_id}: ${r.message}`;
                        });
                    errorMessages.push(...folderErrors);
                }
            }

            // Delete documents using bulk endpoint
            if (selectedDocuments.length > 0) {
                const docResponse = await bulkDeleteDocument(selectedDocuments);
                const { success, failed, results } = docResponse.data;
                totalFailed += failed;

                if (failed > 0) {
                    const docErrors = results
                        .filter((r: any) => r.status === 'error')
                        .map((r: any) => {
                            const doc = documents.find(d => d.id === r.document_id);
                            return `${doc?.name || 'Document ' + r.document_id}: ${r.message}`;
                        });
                    errorMessages.push(...docErrors);
                }
            }

            // Show errors if any
            if (totalFailed > 0) {
                alert(t('Some items failed to delete') + ':\n\n' + errorMessages.join('\n'));
            }

            handleClearSelection();
            loadData();
            loadAllFolders();
        } catch (error: any) {
            alert(error.response?.data?.message || t('Error deleting items'));
        }
    };

    const handleBulkMove = async (targetFolderId: number | null) => {
        if (selectedItems.size === 0) return;

        const selectedFolders: number[] = [];
        const selectedDocuments: number[] = [];

        selectedItems.forEach(key => {
            const [type, id] = key.split('-');
            if (type === 'folder') {
                selectedFolders.push(parseInt(id));
            } else {
                selectedDocuments.push(parseInt(id));
            }
        });

        try {
            // Move folders
            if (selectedFolders.length > 0) {
                await bulkMoveFolder(selectedFolders, targetFolderId);
            }

            // Move documents
            if (selectedDocuments.length > 0) {
                await bulkMoveDocument(selectedDocuments, targetFolderId);
            }

            handleClearSelection();
            loadData();
            loadAllFolders();
        } catch (error: any) {
            alert(error.response?.data?.message || t('Error moving items'));
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getFileIcon = (mimeType: string) => {
        if (mimeType.startsWith('image/')) return 'bi-file-earmark-image';
        if (mimeType.startsWith('video/')) return 'bi-file-earmark-play';
        if (mimeType.startsWith('audio/')) return 'bi-file-earmark-music';
        if (mimeType.includes('pdf')) return 'bi-file-earmark-pdf';
        if (mimeType.includes('word') || mimeType.includes('wordprocessingml')) return 'bi-file-earmark-word';
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'bi-file-earmark-excel';
        if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'bi-file-earmark-ppt';
        if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'bi-file-earmark-zip';
        if (mimeType.includes('apple.pages')) return 'bi-file-earmark-word';
        if (mimeType.includes('apple.numbers')) return 'bi-file-earmark-excel';
        if (mimeType.includes('apple.keynote')) return 'bi-file-earmark-ppt';
        return 'bi-file-earmark';
    };

    const renderFolderTree = (parentId: number | null = null, level: number = 0) => {
        const childFolders = allFolders.filter(f => f.parent_id === parentId);

        return childFolders.map(folder => {
            const isExpanded = expandedFolders.has(folder.id);
            const hasChildren = allFolders.some(f => f.parent_id === folder.id);
            const isActive = currentFolderId === folder.id;
            const isDropTarget = dropTarget === folder.id;

            return (
                <div key={folder.id}>
                    <ListGroup.Item
                        action
                        active={isActive}
                        className={`d-flex align-items-center sidebar-folder-item ${isDropTarget ? 'drop-target' : ''}`}
                        style={{ paddingLeft: `${level * 20 + 10}px`, cursor: canWrite && folder.can_write ? 'grab' : 'pointer' }}
                        draggable={canWrite && folder.can_write}
                        onClick={(e) => {
                            // Only handle click if not dragging
                            if (!draggedItem) {
                                handleFolderClickFromSidebar(folder);
                            }
                        }}
                        onDragStart={(e) => {
                            e.stopPropagation();
                            handleDragStart(e, 'folder', folder);
                        }}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => {
                            e.stopPropagation();
                            handleDragOver(e, folder.id);
                        }}
                        onDragLeave={(e) => {
                            e.stopPropagation();
                            handleDragLeave();
                        }}
                        onDrop={(e) => {
                            e.stopPropagation();
                            handleDrop(e, folder.id);
                        }}
                        onContextMenu={(e) => {
                            e.stopPropagation();
                            handleContextMenu(e, 'folder', folder);
                        }}
                    >
                        {hasChildren && (
                            <i
                                className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedFolders(prev => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(folder.id)) {
                                            newSet.delete(folder.id);
                                        } else {
                                            newSet.add(folder.id);
                                        }
                                        return newSet;
                                    });
                                }}
                                style={{ cursor: 'pointer' }}
                            ></i>
                        )}
                        {!hasChildren && <span className="me-3"></span>}
                        <i className="bi bi-folder-fill me-2"></i>
                        <span className="flex-grow-1 folder-name">{folder.name}</span>
                        {folder.item_count > 0 && (
                            <span className="item-count-badge">{folder.item_count}</span>
                        )}
                        {folder.is_shared === 1 && (
                            <i className="bi bi-people-fill share-icon"></i>
                        )}
                    </ListGroup.Item>
                    {isExpanded && renderFolderTree(folder.id, level + 1)}
                </div>
            );
        });
    };

    const renderSharedFolderTree = (parentId: number | null = null, level: number = 0) => {
        const childFolders = sharedFolders.filter(f => f.parent_id === parentId);

        return childFolders.map(folder => {
            const isExpanded = expandedSharedFolders.has(folder.id);
            const hasChildren = sharedFolders.some(f => f.parent_id === folder.id);
            const isActive = currentFolderId === folder.id;
            const isDropTarget = dropTarget === folder.id;

            return (
                <div key={folder.id}>
                    <ListGroup.Item
                        action
                        active={isActive}
                        className={`d-flex align-items-center sidebar-folder-item ${isDropTarget ? 'drop-target' : ''}`}
                        style={{ paddingLeft: `${level * 20 + 10}px`, cursor: 'pointer' }}
                        onClick={(e) => {
                            if (!draggedItem) {
                                handleFolderClickFromSidebar(folder);
                            }
                        }}
                        onDragOver={(e) => {
                            e.stopPropagation();
                            handleDragOver(e, folder.id);
                        }}
                        onDragLeave={(e) => {
                            e.stopPropagation();
                            handleDragLeave();
                        }}
                        onDrop={(e) => {
                            e.stopPropagation();
                            handleDrop(e, folder.id);
                        }}
                    >
                        {hasChildren && (
                            <i
                                className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedSharedFolders(prev => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(folder.id)) {
                                            newSet.delete(folder.id);
                                        } else {
                                            newSet.add(folder.id);
                                        }
                                        return newSet;
                                    });
                                }}
                                style={{ cursor: 'pointer' }}
                            ></i>
                        )}
                        {!hasChildren && <span className="me-3"></span>}
                        <i className="bi bi-folder-fill me-2 text-primary"></i>
                        <span className="flex-grow-1 folder-name">{folder.name}</span>
                        {folder.item_count > 0 && (
                            <span className="item-count-badge">{folder.item_count}</span>
                        )}
                    </ListGroup.Item>
                    {isExpanded && renderSharedFolderTree(folder.id, level + 1)}
                </div>
            );
        });
    };

    // Filter and sort items
    const getFilteredAndSortedItems = () => {
        let allItems: Array<{ type: 'folder' | 'document', item: Folder | Document }> = [
            ...folders.map(f => ({ type: 'folder' as const, item: f })),
            ...documents.map(d => ({ type: 'document' as const, item: d }))
        ];

        // Filter by search term
        if (searchTerm) {
            allItems = allItems.filter(({ item }) =>
                item.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort
        allItems.sort((a, b) => {
            let comparison = 0;

            if (sortBy === 'name') {
                comparison = a.item.name.localeCompare(b.item.name);
            } else if (sortBy === 'date') {
                const dateA = new Date(a.item.changed_at).getTime();
                const dateB = new Date(b.item.changed_at).getTime();
                comparison = dateB - dateA;
            } else if (sortBy === 'size' && a.type === 'document' && b.type === 'document') {
                const docA = a.item as Document;
                const docB = b.item as Document;
                comparison = docB.file_size - docA.file_size;
            }

            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return allItems;
    };

    const filteredItems = getFilteredAndSortedItems();

    // Calculate current folder stats
    const currentFolderStats = folders.reduce((acc, folder) => {
        return {
            totalItems: acc.totalItems + folder.item_count,
            totalSize: acc.totalSize + folder.total_size
        };
    }, { totalItems: documents.length, totalSize: documents.reduce((sum, doc) => sum + doc.file_size, 0) });

    return (
        <Container fluid className="file-manager-v2">
            <Row className="file-manager-header mb-3">
                <Col>
                    <h1>{t('Document Management')}</h1>
                </Col>
            </Row>

            {/* Admin: User search and viewing banner */}
            {isAdmin && (
                <Row className="mb-3">
                    <Col>
                        {viewingUserId && viewingUser ? (
                            <Alert variant="info" className="mb-0">
                                <div className="d-flex align-items-start justify-content-between">
                                    <div>
                                        <div className="d-flex align-items-center mb-1">
                                            <i className="bi bi-eye me-2"></i>
                                            <span>{t('Viewing documents of')}:</span>
                                            <strong className="ms-1">{viewingUser.name}</strong>
                                            {viewingUser.roles && viewingUser.roles.length > 0 && (
                                                <span className="ms-2">
                                                    {viewingUser.roles.map((role, idx) => (
                                                        <Badge key={idx} bg="secondary" className="me-1">{role}</Badge>
                                                    ))}
                                                </span>
                                            )}
                                        </div>
                                        <div className="small text-muted">
                                            <span>{viewingUser.email}</span>
                                            {viewingUser.clubs && viewingUser.clubs.length > 0 && (
                                                <span className="ms-3">
                                                    <i className="bi bi-building me-1"></i>
                                                    {viewingUser.clubs.join(', ')}
                                                </span>
                                            )}
                                            {viewingUser.teams && viewingUser.teams.length > 0 && (
                                                <span className="ms-3">
                                                    <i className="bi bi-people me-1"></i>
                                                    {viewingUser.teams.join(', ')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <Button variant="outline-primary" size="sm" onClick={handleBackToMyDocuments}>
                                        <i className="bi bi-arrow-left me-1"></i>
                                        {t('Back to my documents')}
                                    </Button>
                                </div>
                            </Alert>
                        ) : (
                            <div className="admin-user-search position-relative">
                                <InputGroup>
                                    <InputGroup.Text>
                                        <i className="bi bi-search"></i>
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('Search user by name, email, team, club or role...')}
                                        value={userSearchTerm}
                                        onChange={(e) => handleUserSearch(e.target.value)}
                                    />
                                    {searchingUsers && (
                                        <InputGroup.Text>
                                            <Spinner animation="border" size="sm" />
                                        </InputGroup.Text>
                                    )}
                                </InputGroup>
                                {userSearchResults.length > 0 && (
                                    <ListGroup className="position-absolute w-100 shadow" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                        {userSearchResults.map(user => (
                                            <ListGroup.Item
                                                key={user.id}
                                                action
                                                onClick={() => handleSelectUser(user)}
                                                className="py-2"
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <div className="fw-medium">
                                                            <i className="bi bi-person me-2"></i>
                                                            {user.name}
                                                            {user.roles && user.roles.length > 0 && (
                                                                <span className="ms-2">
                                                                    {user.roles.map((role, idx) => (
                                                                        <Badge key={idx} bg="secondary" className="me-1">{role}</Badge>
                                                                    ))}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <small className="text-muted">{user.email}</small>
                                                    </div>
                                                    <div className="text-end">
                                                        {user.clubs && user.clubs.length > 0 && (
                                                            <div>
                                                                <small className="text-muted">
                                                                    <i className="bi bi-building me-1"></i>
                                                                    {user.clubs.join(', ')}
                                                                </small>
                                                            </div>
                                                        )}
                                                        {user.teams && user.teams.length > 0 && (
                                                            <div>
                                                                <small className="text-muted">
                                                                    <i className="bi bi-people me-1"></i>
                                                                    {user.teams.join(', ')}
                                                                </small>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            )}

            <Row className="file-manager-body">
                {/* Sidebar */}
                <Col md={3} className="file-manager-sidebar">
                    {/* My Documents Section - Only for managers and coaches */}
                    {canWrite && (
                        <>
                            <div className="sidebar-header">
                                <h6>{t('Folders')}</h6>
                            </div>
                            <ListGroup variant="flush" className="sidebar-list">
                                <ListGroup.Item
                                    action
                                    active={currentFolderId === null}
                                    className={`sidebar-folder-item ${dropTarget === null && draggedItem !== null ? 'drop-target' : ''}`}
                                    onClick={() => {
                                        setCurrentFolderId(null);
                                        setFolderPath([]);
                                    }}
                                    onDragOver={(e) => {
                                        e.stopPropagation();
                                        handleDragOver(e, null);
                                    }}
                                    onDragLeave={(e) => {
                                        e.stopPropagation();
                                        handleDragLeave();
                                    }}
                                    onDrop={(e) => {
                                        e.stopPropagation();
                                        handleDrop(e, null);
                                    }}
                                    onContextMenu={(e) => {
                                        if (canWriteInCurrentFolder) {
                                            e.stopPropagation();
                                            handleContextMenu(e, 'background');
                                        }
                                    }}
                                >
                                    <i className="bi bi-house-door me-2"></i>
                                    <span className="folder-name">{t('My Documents')}</span>
                                </ListGroup.Item>
                                {renderFolderTree()}
                            </ListGroup>
                        </>
                    )}

                    {/* Shared Folders Section */}
                    {sharedFolders.length > 0 && (
                        <>
                            <div className={`sidebar-header ${canWrite ? 'mt-3' : ''}`}>
                                <h6>{t('Shared with me')}</h6>
                            </div>
                            <ListGroup variant="flush" className="sidebar-list">
                                {renderSharedFolderTree()}
                            </ListGroup>
                        </>
                    )}
                </Col>

                {/* Main Content */}
                <Col md={9} className="file-manager-content">
                    {/* Selection Toolbar */}
                    {selectedItems.size > 0 && (
                        <div className="selection-toolbar">
                            <div className="selection-info">
                                <Form.Check
                                    type="checkbox"
                                    checked={selectedItems.size === filteredItems.length}
                                    onChange={handleSelectAll}
                                    label={`${selectedItems.size} ${t('selected')}`}
                                    className="me-3"
                                />
                            </div>
                            <div className="selection-actions">
                                <button className="btn btn-sm btn-outline-primary me-2" onClick={handleBulkDownload}>
                                    <i className="bi bi-download me-1"></i>
                                    {t('Download selected')}
                                </button>
                                <button className="btn btn-sm btn-outline-secondary me-2" onClick={handleClearSelection}>
                                    <i className="bi bi-x-circle me-1"></i>
                                    {t('Clear selection')}
                                </button>
                                {canWrite && (
                                    <button className="btn btn-sm btn-outline-danger" onClick={handleBulkDelete}>
                                        <i className="bi bi-trash me-1"></i>
                                        {t('Delete selected')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Toolbar */}
                    <div className="content-toolbar">
                        <div className="toolbar-left">
                            <BreadcrumbNav path={folderPath} onNavigate={handleBreadcrumbClick} />
                        </div>
                        <div className="toolbar-right">
                            <InputGroup size="sm" className="search-input">
                                <InputGroup.Text>
                                    <i className="bi bi-search"></i>
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder={t('Search...')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>

                            <Form.Select
                                size="sm"
                                className="sort-select"
                                value={`${sortBy}-${sortOrder}`}
                                onChange={(e) => {
                                    const [newSortBy, newSortOrder] = e.target.value.split('-');
                                    setSortBy(newSortBy as SortBy);
                                    setSortOrder(newSortOrder as SortOrder);
                                }}
                            >
                                <option value="name-asc">{t('Name')} (A-Z)</option>
                                <option value="name-desc">{t('Name')} (Z-A)</option>
                                <option value="date-desc">{t('Date')} ({t('Newest')})</option>
                                <option value="date-asc">{t('Date')} ({t('Oldest')})</option>
                                <option value="size-desc">{t('Size')} ({t('Largest')})</option>
                                <option value="size-asc">{t('Size')} ({t('Smallest')})</option>
                            </Form.Select>
                        </div>
                    </div>

                    {/* Folder Info (Shared by and Description) */}
                    {currentFolderId && (() => {
                        const currentFolder = [...allFolders, ...sharedFolders].find(f => f.id === currentFolderId);
                        const showSharedBy = currentFolder && !currentFolder.is_owner && currentFolder.created_by_name;
                        const showDescription = currentFolder && currentFolder.description;

                        if (showSharedBy || showDescription) {
                            return (
                                <div className="folder-info mb-3 p-3 bg-light border-top border-bottom">
                                    {showSharedBy && (
                                        <div className="folder-owner mb-2">
                                            <small className="text-muted">
                                                <i className="bi bi-person-fill me-1"></i>
                                                {t('Shared by')}: <strong>{currentFolder.created_by_name}</strong>
                                            </small>
                                        </div>
                                    )}
                                    {showDescription && (
                                        <div className="folder-description">
                                            <small className="text-muted">{currentFolder.description}</small>
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        return null;
                    })()}

                    {/* File List */}
                    <div
                        className={`file-list-container ${isDraggingFiles ? 'dragging-files' : ''}`}
                        ref={contentAreaRef}
                        onContextMenu={(e) => canWriteInCurrentFolder && handleContextMenu(e, 'background')}
                        onDragOver={(e) => {
                            if (e.dataTransfer.types.includes('Files')) {
                                // Dragging files from explorer
                                handleFileDragOver(e);
                            } else if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.empty-state')) {
                                // Dragging internal items
                                handleDragOver(e, currentFolderId);
                            }
                        }}
                        onDragLeave={handleFileDragLeave}
                        onDrop={(e) => {
                            if (e.dataTransfer.types.includes('Files')) {
                                // Dropping files from explorer
                                handleFileDrop(e);
                            } else if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.empty-state')) {
                                // Dropping internal items
                                handleDrop(e, currentFolderId);
                            }
                        }}
                    >
                        {/* Upload overlay */}
                        {uploadingFiles && (
                            <div className="upload-overlay">
                                <Spinner animation="border" />
                                <p className="mt-3">{t('Uploading files...')}</p>
                            </div>
                        )}

                        {/* Drag overlay */}
                        {isDraggingFiles && canWriteInCurrentFolder && (
                            <div className="drag-overlay">
                                <i className="bi bi-cloud-upload"></i>
                                <p>{t('Drop files here to upload')}</p>
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center mt-5">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">{t('Loading...')}</span>
                                </Spinner>
                            </div>
                        ) : (
                            <>
                                {/* Current folder info */}
                                {currentFolderId && folderPath.length > 0 && folderPath[folderPath.length - 1].description && (
                                    <div className="current-folder-info">
                                        <div className="folder-description-full">
                                            {folderPath[folderPath.length - 1].description}
                                        </div>
                                    </div>
                                )}

                                {filteredItems.length === 0 ? (
                                    <div className="empty-state">
                                        <i className="bi bi-inbox"></i>
                                        <p>{searchTerm ? t('No results found') : t('This folder is empty')}</p>
                                    </div>
                                ) : (
                                    <ListGroup variant="flush" className="file-list">
                                        {filteredItems.map(({ type, item }, index) => {
                                            const isDropTargetFolder = type === 'folder' && dropTarget === (item as Folder).id;
                                            const itemKey = getItemKey(type, item.id);
                                            const isSelected = selectedItems.has(itemKey);
                                            return (
                                            <ListGroup.Item
                                                key={`${type}-${item.id}`}
                                                className={`file-item ${isDropTargetFolder ? 'drop-target-folder' : ''} ${isSelected ? 'selected' : ''}`}
                                                draggable={canWrite && (type === 'folder' ? (item as Folder).can_write : (item as Document).can_write)}
                                                onDragStart={(e) => handleDragStart(e, type, item)}
                                                onDragEnd={handleDragEnd}
                                                onDragOver={(e) => {
                                                    if (type === 'folder') {
                                                        e.stopPropagation();
                                                        handleDragOver(e, (item as Folder).id);
                                                    }
                                                }}
                                                onDragLeave={(e) => {
                                                    if (type === 'folder') {
                                                        e.stopPropagation();
                                                        handleDragLeave();
                                                    }
                                                }}
                                                onDrop={(e) => {
                                                    if (type === 'folder') {
                                                        e.stopPropagation();
                                                        handleDrop(e, (item as Folder).id);
                                                    }
                                                }}
                                                onClick={(e) => {
                                                    // Only select with Ctrl/Cmd+Click or Shift+Click (for quick multi-selection)
                                                    if (e.ctrlKey || e.metaKey || e.shiftKey) {
                                                        handleItemSelect(e, type, item, index);
                                                    }
                                                }}
                                                onDoubleClick={() => type === 'folder' ? handleFolderClick(item as Folder) : handleDownloadDocument(item as Document)}
                                                onContextMenu={(e) => {
                                                    e.stopPropagation();
                                                    handleContextMenu(e, type, item);
                                                }}
                                            >
                                                <div className="file-checkbox">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleCheckboxChange(type, item.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="file-icon">
                                                    {type === 'folder' ? (
                                                        <i className="bi bi-folder-fill"></i>
                                                    ) : (
                                                        <i className={`bi ${getFileIcon((item as Document).mime_type)}`}></i>
                                                    )}
                                                </div>
                                                <div className="file-name">
                                                    <div className="file-name-row">
                                                        <span className="name-text">{item.name}</span>
                                                        {item.is_shared === 1 && (
                                                            <i className="bi bi-people-fill share-icon ms-2"></i>
                                                        )}
                                                        {type === 'folder' && (item as Folder).item_count > 0 && (
                                                            <span className="item-count-badge ms-2">{(item as Folder).item_count}</span>
                                                        )}
                                                    </div>
                                                    {type === 'folder' && (item as Folder).description && (
                                                        <div className="file-description">
                                                            {(item as Folder).description.length > 50
                                                                ? (item as Folder).description.substring(0, 50) + '...'
                                                                : (item as Folder).description}
                                                        </div>
                                                    )}
                                                    {type === 'document' && (item as Document).description && (
                                                        <div className="file-description">
                                                            {(item as Document).description.length > 50
                                                                ? (item as Document).description.substring(0, 50) + '...'
                                                                : (item as Document).description}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="file-modified">
                                                    {moment(item.changed_at).format('DD/MM/YYYY HH:mm')}
                                                </div>
                                                <div className="file-size">
                                                    {type === 'document' ? formatFileSize((item as Document).file_size) : formatFileSize((item as Folder).total_size)}
                                                </div>
                                            </ListGroup.Item>
                                            );
                                        })}
                                    </ListGroup>
                                )}
                            </>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Context Menu */}
            {contextMenu && (
                <div
                    className="context-menu"
                    style={{
                        position: 'fixed',
                        top: contextMenu.y,
                        left: contextMenu.x,
                        zIndex: 9999
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {contextMenu.type === 'background' && (
                        <>
                            {selectedItems.size > 0 ? (
                                // Menu for selected items
                                <>
                                    <div className="context-menu-item" onClick={handleBulkDownload}>
                                        <i className="bi bi-download"></i>
                                        <span>{t('Download selected')}</span>
                                    </div>
                                    {canWrite && (
                                        <>
                                            <div className="context-menu-divider"></div>
                                            <div className="context-menu-item danger" onClick={handleBulkDelete}>
                                                <i className="bi bi-trash"></i>
                                                <span>{t('Delete selected')}</span>
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                // Menu for empty area (no selection)
                                canWriteInCurrentFolder && (
                                    <div className="context-menu-item" onClick={handleNewFolder}>
                                        <i className="bi bi-folder-plus"></i>
                                        <span>{t('New Folder')}</span>
                                    </div>
                                )
                            )}
                        </>
                    )}

                    {contextMenu.type === 'folder' && contextMenu.item && (
                        <>
                            <div className="context-menu-item" onClick={() => handleDownloadFolder(contextMenu.item as Folder)}>
                                <i className="bi bi-download"></i>
                                <span>{t('Download')}</span>
                            </div>
                            {(contextMenu.item as Folder).can_write && canWrite && (
                                <>
                                    <div className="context-menu-divider"></div>
                                    <div className="context-menu-item" onClick={() => handleNewSubfolder(contextMenu.item as Folder)}>
                                        <i className="bi bi-folder-plus"></i>
                                        <span>{t('New Folder')}</span>
                                    </div>
                                    <div className="context-menu-divider"></div>
                                    <div className="context-menu-item" onClick={() => handleEditFolder(contextMenu.item as Folder)}>
                                        <i className="bi bi-pencil"></i>
                                        <span>{t('Edit')}</span>
                                    </div>
                                    <div className="context-menu-item" onClick={() => handleShare('folder', contextMenu.item as Folder)}>
                                        <i className="bi bi-share"></i>
                                        <span>{t('Share')}</span>
                                    </div>
                                    <div className="context-menu-divider"></div>
                                    <div className="context-menu-item danger" onClick={() => handleDeleteFolder(contextMenu.item as Folder)}>
                                        <i className="bi bi-trash"></i>
                                        <span>{t('Delete')}</span>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {contextMenu.type === 'document' && contextMenu.item && (
                        <>
                            <div className="context-menu-item" onClick={() => handleDownloadDocument(contextMenu.item as Document)}>
                                <i className="bi bi-download"></i>
                                <span>{t('Download')}</span>
                            </div>
                            {(contextMenu.item as Document).can_write && canWrite && (
                                <>
                                    <div className="context-menu-divider"></div>
                                    <div className="context-menu-item" onClick={() => handleEditDocument(contextMenu.item as Document)}>
                                        <i className="bi bi-pencil"></i>
                                        <span>{t('Edit')}</span>
                                    </div>
                                    <div className="context-menu-item" onClick={() => handleShare('document', contextMenu.item as Document)}>
                                        <i className="bi bi-share"></i>
                                        <span>{t('Share')}</span>
                                    </div>
                                    <div className="context-menu-divider"></div>
                                    <div className="context-menu-item danger" onClick={() => handleDeleteDocument(contextMenu.item as Document)}>
                                        <i className="bi bi-trash"></i>
                                        <span>{t('Delete')}</span>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Modals */}
            <FolderModal
                show={showFolderModal}
                onClose={() => {
                    setShowFolderModal(false);
                    setEditingFolder(null);
                }}
                onSave={() => {
                    setShowFolderModal(false);
                    setEditingFolder(null);
                    loadData();
                    loadAllFolders();
                }}
                folder={editingFolder}
                parentId={currentFolderId}
            />

            <DocumentModal
                show={showDocumentModal}
                onClose={() => {
                    setShowDocumentModal(false);
                    setEditingDocument(null);
                }}
                onSave={() => {
                    setShowDocumentModal(false);
                    setEditingDocument(null);
                    loadData();
                }}
                document={editingDocument}
            />

            {shareItem && (
                <ShareModal
                    show={showShareModal}
                    onClose={() => {
                        setShowShareModal(false);
                        setShareItem(null);
                    }}
                    onShare={() => {
                        loadData();
                    }}
                    type={shareItem.type}
                    item={shareItem.item}
                />
            )}
        </Container>
    );
}
